import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, playerApi, getPlayer, getPlayerToken, setPlayer, clearPlayer } from "../lib/api";
import { DIAL_CODES } from "../lib/dialCodes";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Gift, Clock, CheckCircle2, X } from "lucide-react";
import InstallButton from "../components/InstallButton";

const LOGO = "/assets/brand/logo.png";
const GAME_LABEL = { roue: "Roue", grattage: "Grattage", memory: "Memory", runner: "Manicou Runner", manuel: "Cadeau", bo: "Cadeau" };
const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return "—"; }
};
const daysLeft = (s) => {
  if (!s) return null;
  const d = Math.ceil((new Date(s).getTime() - Date.now()) / 86400000);
  return d;
};
const STATUS = {
  valid: { txt: "Valide", cls: "bg-[#e9f9ef] text-[#1a7a3c]" },
  redeemed: { txt: "Utilisé", cls: "bg-gray-100 text-gray-500" },
  expired: { txt: "Expiré", cls: "bg-red-50 text-red-500" },
  disabled: { txt: "Désactivé", cls: "bg-red-50 text-red-500" },
};
const TIER = {
  vip: { txt: "VIP", emoji: "👑", cls: "bg-[#F9B81E] text-[#1a1206]" },
  recurrent: { txt: "Client fidèle", emoji: "⭐", cls: "bg-[#FFE1B0] text-[#8a4b00]" },
  ponctuel: { txt: "Habitué", emoji: "🙂", cls: "bg-[#FFF3D6] text-[#a86400]" },
};

/* ---------- Écran connexion / inscription / mot de passe oublié ---------- */
function AuthForm({ onLogged }) {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", dial: "+596", number: "", password: "", consent: false });
  const [busy, setBusy] = useState(false);
  const [resetStep, setResetStep] = useState("request"); // request | verify
  const [resetCode, setResetCode] = useState("");
  const [newPwd, setNewPwd] = useState("");

  const phoneStr = () => `${form.dial} ${form.number.replace(/\s/g, "")}`;
  const inp = "w-full border-2 border-gray-200 rounded-2xl px-4 py-3 focus:border-[#F9B81E] outline-none";

  const submit = async () => {
    const digits = form.number.replace(/\D/g, "");
    if (digits.length < 6) { toast.error("Numéro invalide"); return; }
    if (form.password.length < 4) { toast.error("Mot de passe : 4 caractères minimum"); return; }
    if (mode === "register" && !form.first_name.trim()) { toast.error("Ton prénom est requis"); return; }
    setBusy(true);
    try {
      const body = mode === "register"
        ? { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: phoneStr(), password: form.password, consent: form.consent }
        : { phone: phoneStr(), password: form.password };
      const { data } = await api.post(mode === "register" ? "/auth/register" : "/auth/login", body);
      setPlayer(data.token, { phone: data.phone, first_name: data.first_name });
      toast.success(mode === "register" ? "Compte créé 🎉" : `Rebonjour ${data.first_name} !`);
      onLogged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const requestCode = async () => {
    if (form.number.replace(/\D/g, "").length < 6) { toast.error("Entre ton numéro"); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot", { phone: phoneStr() });
      toast.success("Si un compte existe, un code arrive sur WhatsApp 📲");
      setResetStep("verify");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    if (!resetCode.trim()) { toast.error("Entre le code reçu"); return; }
    if (newPwd.length < 4) { toast.error("Nouveau mot de passe : 4 caractères min."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset", { phone: phoneStr(), code: resetCode.trim(), password: newPwd });
      setPlayer(data.token, { phone: data.phone, first_name: data.first_name });
      toast.success("Mot de passe changé 🎉");
      onLogged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const phoneRow = (
    <div className="flex gap-2">
      <select value={form.dial} onChange={(e) => setForm({ ...form, dial: e.target.value })} className="border-2 border-gray-200 rounded-2xl px-2 py-3 bg-white text-sm max-w-[110px] outline-none focus:border-[#F9B81E]">
        {DIAL_CODES.map((c, i) => (<option key={c.code + i} value={c.code}>{c.flag} {c.code}</option>))}
      </select>
      <input type="tel" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="Ton numéro (ex. 0696…)" className={`${inp} flex-1 min-w-0`} />
    </div>
  );

  if (mode === "forgot") {
    return (
      <div className="w-full max-w-sm mx-auto bg-white rounded-3xl p-6 shadow-xl">
        <h3 className="font-black text-lg text-gray-800 mb-1">Mot de passe oublié 🔑</h3>
        <p className="text-xs text-gray-500 mb-4">
          {resetStep === "request"
            ? "Entre ton numéro : on t'envoie un code par WhatsApp pour choisir un nouveau mot de passe."
            : "Entre le code reçu sur WhatsApp et ton nouveau mot de passe."}
        </p>
        <div className="flex flex-col gap-3">
          {phoneRow}
          {resetStep === "request" ? (
            <button onClick={requestCode} disabled={busy} data-testid="forgot-request" className="w-full bg-[#E20613] text-white font-black py-3.5 rounded-2xl hover:bg-[#c40510] disabled:opacity-60">
              {busy ? "…" : "Recevoir mon code 📲"}
            </button>
          ) : (
            <>
              <input value={resetCode} onChange={(e) => setResetCode(e.target.value)} inputMode="numeric" placeholder="Code à 6 chiffres" className={`${inp} text-center tracking-[0.4em] font-bold`} data-testid="reset-code" />
              <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Nouveau mot de passe" className={inp} onKeyDown={(e) => e.key === "Enter" && doReset()} data-testid="reset-newpwd" />
              <button onClick={doReset} disabled={busy} data-testid="reset-submit" className="w-full bg-[#E20613] text-white font-black py-3.5 rounded-2xl hover:bg-[#c40510] disabled:opacity-60">
                {busy ? "…" : "Changer mon mot de passe"}
              </button>
              <button onClick={requestCode} disabled={busy} className="text-xs text-gray-400 hover:text-gray-600">Renvoyer un code</button>
            </>
          )}
          <button onClick={() => { setMode("login"); setResetStep("request"); setResetCode(""); setNewPwd(""); }} className="text-xs text-gray-400 hover:text-gray-600 mt-1">← Retour à la connexion</button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm mx-auto bg-white rounded-3xl p-6 shadow-xl">
      <div className="flex gap-2 bg-gray-100 rounded-2xl p-1 mb-5">
        {["login", "register"].map((m) => (
          <button key={m} onClick={() => setMode(m)}
            className={`flex-1 py-2.5 rounded-xl font-black text-sm ${mode === m ? "bg-[#E20613] text-white" : "text-gray-500"}`}>
            {m === "login" ? "Connexion" : "Créer un compte"}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        {mode === "register" && (
          <>
            <div className="flex gap-2">
              <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="Prénom" className={inp} />
              <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Nom" className={inp} />
            </div>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-mail (utile en cas d'oubli)" className={inp} />
          </>
        )}
        {phoneRow}
        <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe" className={inp} onKeyDown={(e) => e.key === "Enter" && submit()} />
        {mode === "register" && (
          <label className="flex items-start gap-2 text-xs text-gray-500">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" />
            <span>J'accepte de recevoir les offres YÜM par WhatsApp.</span>
          </label>
        )}
        <button onClick={submit} disabled={busy} className="w-full bg-[#E20613] text-white font-black py-3.5 rounded-2xl hover:bg-[#c40510] disabled:opacity-60">
          {busy ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte 🎉"}
        </button>
        {mode === "login" && (
          <button onClick={() => setMode("forgot")} data-testid="forgot-link" className="text-xs text-gray-400 hover:text-[#E20613] text-center">Mot de passe oublié ?</button>
        )}
      </div>
      <p className="text-center text-xs text-gray-400 mt-4">Ton compte garde tes bons <b>10 jours</b> et regroupe tous tes cadeaux, sur n'importe quel appareil.</p>
    </div>
  );
}

/* ---------- Carte d'un bon ---------- */
function VoucherCard({ v, onShowQr }) {
  const st = STATUS[v.status] || STATUS.valid;
  const dl = daysLeft(v.expires_at);
  const urgent = v.status === "valid" && dl != null && dl <= 3;
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-black/5 flex items-center gap-3">
      <button onClick={() => onShowQr(v)} className="flex-none w-16 h-16 rounded-xl bg-white border-2 border-[#F9B81E] overflow-hidden grid place-items-center">
        {v.qr ? <img src={v.qr} alt="QR" className="w-full h-full object-contain" /> : <Gift size={22} className="text-[#E20613]" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="font-black text-[#1a1206] leading-tight truncate">{v.label}</p>
        <p className="text-[11px] text-gray-400">{GAME_LABEL[v.game] || v.game}</p>
        <p className={`text-xs mt-0.5 ${urgent ? "text-[#E20613] font-bold" : "text-gray-500"}`}>
          {v.status === "valid" ? <>Valable jusqu'au <b>{fmtDate(v.expires_at)}</b>{urgent && dl >= 0 ? ` · ${dl} j restant${dl > 1 ? "s" : ""} !` : ""}</> : `Gagné le ${fmtDate(v.claimed_at)}`}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className={`text-[10px] font-black px-2 py-1 rounded-full ${st.cls}`}>{st.txt}</span>
        <span className="font-mono text-[11px] text-gray-400">{v.code}</span>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function AccountPage() {
  const [player, setPlayerState] = useState(getPlayer());
  const [data, setData] = useState(null); // { profile, vouchers, stats }
  const [loading, setLoading] = useState(true);
  const [qr, setQr] = useState(null); // bon dont on affiche le QR en grand
  const loggedIn = !!getPlayerToken() && !!player;

  const load = useCallback(async () => {
    if (!getPlayerToken()) { setLoading(false); return; }
    try {
      const { data } = await playerApi().get(`/auth/me?origin=${encodeURIComponent(window.location.origin)}`);
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) { clearPlayer(); setPlayerState(null); }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onLogged = () => { setPlayerState(getPlayer()); setLoading(true); load(); };
  const logout = () => { clearPlayer(); setPlayerState(null); setData(null); };

  const first = data?.profile?.first_name || player?.first_name || "";
  const tier = data?.stats?.tier ? TIER[data.stats.tier] : null;
  const vouchers = data?.vouchers || [];
  const s = data?.stats || { valides: 0, utilises: 0, total: 0 };

  return (
    <div className="min-h-screen w-full bg-[#FFF6E9] text-[#1a1206] flex flex-col items-center">
      {/* En-tête rouge YÜM */}
      <div className="w-full bg-[#E20613] text-white">
        <div className="w-full max-w-md mx-auto px-4 pt-4 pb-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1 text-white/90 text-sm font-bold bg-white/15 px-3 py-1.5 rounded-full"><ArrowLeft size={15} /> Accueil</Link>
            <img src={LOGO} alt="YÜM" className="h-9" />
            {loggedIn ? (
              <button onClick={logout} className="flex items-center gap-1 text-white/90 text-sm font-bold bg-white/15 px-3 py-1.5 rounded-full"><LogOut size={15} /> Quitter</button>
            ) : <span className="w-16" />}
          </div>

          {loggedIn && (
            <div className="mt-5">
              <p className="text-white/80 text-sm">Bonjour</p>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-3xl leading-tight">{first || "toi"} 👋</h1>
                {tier && <span className={`text-xs font-black px-2.5 py-1 rounded-full ${tier.cls}`}>{tier.emoji} {tier.txt}</span>}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full max-w-md mx-auto px-4 -mt-3 pb-16">
        {!loggedIn ? (
          <div className="mt-8">
            <h1 className="font-display text-2xl text-center mb-1">Mon compte</h1>
            <p className="text-center text-sm text-gray-500 mb-5">Connecte-toi pour retrouver tous tes bons 🎁</p>
            <AuthForm onLogged={onLogged} />
          </div>
        ) : loading ? (
          <p className="text-center text-gray-400 mt-16">Chargement…</p>
        ) : (
          <>
            {/* Tuiles stats (style Pongo) */}
            <div className="grid grid-cols-3 gap-2.5">
              {[
                { n: s.valides, l: "Bons valides", icon: <Gift size={16} className="text-[#E20613]" /> },
                { n: s.utilises, l: "Utilisés", icon: <CheckCircle2 size={16} className="text-[#1a7a3c]" /> },
                { n: s.total, l: "Au total", icon: <Clock size={16} className="text-[#F9B81E]" /> },
              ].map((t) => (
                <div key={t.l} className="bg-white rounded-2xl p-3 text-center shadow-sm border border-black/5">
                  <div className="flex justify-center mb-1">{t.icon}</div>
                  <div className="text-2xl font-black leading-none">{t.n}</div>
                  <div className="text-[10px] text-gray-400 mt-1">{t.l}</div>
                </div>
              ))}
            </div>

            {/* Liste des bons */}
            <h2 className="font-display text-xl mt-6 mb-3">Mes bons 🎟️</h2>
            {vouchers.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center text-gray-400 shadow-sm">
                Pas encore de bon. <Link to="/" className="text-[#E20613] font-bold">Joue pour en gagner !</Link>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                {vouchers.map((v) => <VoucherCard key={v.code} v={v} onShowQr={setQr} />)}
              </div>
            )}

            {/* Installer la PWA sur l'écran d'accueil */}
            <div className="mt-6 flex justify-center">
              <InstallButton kind="games" label="📲 Ajouter l'app à l'écran d'accueil"
                className="w-full max-w-xs bg-[#F9B81E] text-[#1a1206] font-black py-3 rounded-2xl text-center shadow-sm" />
            </div>
          </>
        )}
      </div>

      {/* QR en grand */}
      {qr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setQr(null)}>
          <div className="bg-white rounded-3xl p-6 max-w-xs w-full text-center relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setQr(null)} className="absolute top-3 right-3 text-gray-400"><X size={22} /></button>
            <p className="font-black text-lg text-[#F26C1E] mb-3">{qr.label}</p>
            <img src={qr.qr} alt="QR" className="mx-auto w-52 h-52 rounded-xl border-4 border-[#F9B81E]" />
            <div className="bg-[#FFF3D6] border-2 border-dashed border-[#F9B81E] rounded-xl py-2 mt-3">
              <p className="text-xl font-black tracking-widest text-[#E20613]">{qr.code}</p>
            </div>
            {qr.status === "valid"
              ? <p className="text-xs text-gray-500 mt-2">Valable jusqu'au <b>{fmtDate(qr.expires_at)}</b></p>
              : <p className="text-xs text-gray-400 mt-2">{(STATUS[qr.status] || {}).txt}</p>}
            <p className="text-[11px] text-gray-400 mt-2">Montre ce QR en caisse.</p>
          </div>
        </div>
      )}
    </div>
  );
}
